import React, { useState } from 'react';
import { Check } from 'lucide-react';

const DAYS_OF_WEEK = [
  { id: 'monday', label: 'Monday', short: 'Mon' },
  { id: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { id: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { id: 'thursday', label: 'Thursday', short: 'Thu' },
  { id: 'friday', label: 'Friday', short: 'Fri' },
  { id: 'saturday', label: 'Saturday', short: 'Sat' },
  { id: 'sunday', label: 'Sunday', short: 'Sun' },
];

const ScheduleSurveyModal = ({ isOpen, plan, darkMode, theme, onComplete, onCancel }) => {
  const minDays = plan?.runs || 3;
  const [daysPerWeek, setDaysPerWeek] = useState(minDays);
  const [selectedDays, setSelectedDays] = useState([]);
  const [step, setStep] = useState(1);

  if (!isOpen || !plan) return null;

  const handleDayToggle = (dayId) => {
    setSelectedDays(prev => {
      if (prev.includes(dayId)) {
        return prev.filter(d => d !== dayId);
      } else {
        return [...prev, dayId];
      }
    });
  };

  const handleContinue = () => {
    if (step === 1) {
      setStep(2);
    } else {
      // Complete the survey
      onComplete({ daysPerWeek, selectedDays });
    }
  };

  const canContinueStep1 = daysPerWeek >= minDays;
  const canContinueStep2 = selectedDays.length >= daysPerWeek;

  const modalClass = darkMode
    ? 'bg-slate-900 text-white border border-slate-700'
    : 'bg-white text-slate-900 border border-slate-200';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      onClick={onCancel}
    >
      <div
        className={`relative w-full max-w-lg max-h-[90vh] rounded-3xl shadow-2xl flex flex-col ${modalClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div className="flex-shrink-0 p-6 pb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${theme.primary}`}>
              {step}
            </div>
            <div className="flex-1 h-1 bg-slate-200 dark:bg-slate-700 rounded-full">
              <div
                className={`h-full rounded-full transition-all ${theme.primary}`}
                style={{ width: step === 1 ? '50%' : '100%' }}
              />
            </div>
          </div>
          <h2 className="text-2xl font-bold">
            {plan.name}
          </h2>
          <p className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-600'}>
            Let's customize your schedule
          </p>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6">
          {/* Step 1: Days per week */}
          {step === 1 && (
            <div className="pb-4">
            <h3 className="text-lg font-semibold mb-3">
              How many days per week do you want to run?
            </h3>
            <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              This plan recommends at least {minDays} days per week
            </p>

            <div className="grid grid-cols-5 gap-2">
              {[3, 4, 5, 6, 7].map(days => {
                const isSelected = daysPerWeek === days;
                const isDisabled = days < minDays;

                return (
                  <button
                    key={days}
                    onClick={() => !isDisabled && setDaysPerWeek(days)}
                    disabled={isDisabled}
                    className={`
                      relative py-4 rounded-xl font-semibold text-lg transition-all
                      ${isDisabled
                        ? (darkMode ? 'bg-slate-800 text-slate-600 cursor-not-allowed opacity-40' : 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-40')
                        : isSelected
                          ? `${theme.primary} text-white shadow-lg scale-105`
                          : darkMode
                            ? 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                      }
                    `}
                  >
                    {days}
                    {isSelected && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-emerald-500" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

              {daysPerWeek < minDays && (
                <p className="text-xs text-amber-500 mt-3">
                  ⚠️ This plan requires at least {minDays} running days
                </p>
              )}
            </div>
          )}

          {/* Step 2: Which days */}
          {step === 2 && (
            <div className="pb-4">
            <h3 className="text-lg font-semibold mb-3">
              Which days of the week are you available?
            </h3>
            <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Select at least {daysPerWeek} days ({selectedDays.length}/{daysPerWeek} selected)
            </p>

            <div className="space-y-2">
              {DAYS_OF_WEEK.map(day => {
                const isSelected = selectedDays.includes(day.id);

                return (
                  <button
                    key={day.id}
                    onClick={() => handleDayToggle(day.id)}
                    className={`
                      w-full flex items-center justify-between p-4 rounded-xl font-medium transition-all
                      ${isSelected
                        ? `${theme.primary} text-white shadow-md`
                        : darkMode
                          ? 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                      }
                    `}
                  >
                    <span>{day.label}</span>
                    {isSelected && (
                      <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-emerald-500" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

              {selectedDays.length < daysPerWeek && (
                <p className="text-xs text-amber-500 mt-3">
                  ⚠️ Please select at least {daysPerWeek} days to continue
                </p>
              )}
            </div>
          )}
        </div>

        {/* Actions - Fixed at bottom */}
        <div className="flex-shrink-0 p-6 pt-4 border-t" style={{ borderColor: darkMode ? '#334155' : '#e2e8f0' }}>
          <div className="flex gap-3">
          {step === 2 && (
            <button
              onClick={() => setStep(1)}
              className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
                darkMode
                  ? 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
              }`}
            >
              Back
            </button>
          )}
          <button
            onClick={handleContinue}
            disabled={(step === 1 && !canContinueStep1) || (step === 2 && !canContinueStep2)}
            className={`flex-1 py-3 rounded-xl font-medium text-white transition-colors ${
              theme.primary
            } ${
              ((step === 1 && !canContinueStep1) || (step === 2 && !canContinueStep2))
                ? 'opacity-50 cursor-not-allowed'
                : theme.hover
            }`}
          >
            {step === 1 ? 'Continue' : 'Start Plan'}
            </button>
          </div>

          {step === 1 && (
            <button
              onClick={onCancel}
              className="w-full mt-3 py-2 text-sm opacity-70 hover:opacity-100"
            >
              Skip for now
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleSurveyModal;
